import { useState, useEffect, useRef } from 'react';
import { Send, MessageCircle, ShieldAlert } from 'lucide-react';
import type { Conversation, DirectMessage } from '../stores/messageStore';

const REPORT_EMAIL = 'admin@stamp-fit.com';

interface Props {
  conversation: Conversation;
  messages: DirectMessage[];
  currentUserId: string;
  onSend: (body: string) => Promise<void>;
}

export default function ChatThread({ conversation, messages, currentUserId, onSend }: Props) {
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  const partner = conversation.partner_name || conversation.partner_email || '대화 상대';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await onSend(trimmed);
      setInput('');
    } catch (err: any) {
      alert(err.message || '메시지 전송에 실패했습니다.');
    } finally {
      setSending(false);
    }
  };

  const reportSubject = encodeURIComponent(`[StampFit 신고] 부적절한 메시지 - 대화 ID ${conversation.id}`);
  const reportBody = encodeURIComponent(
    `안녕하세요, 아래 대화에서 부적절한 메시지를 받았습니다.\n\n` +
    `- 대화 상대: ${partner}\n` +
    `- 대화 ID: ${conversation.id}\n` +
    `- 신고 사유: (직접 작성해주세요)\n\n` +
    `※ 해당 메시지의 스크린샷을 첨부해주세요.`,
  );
  const reportMailto = `mailto:${REPORT_EMAIL}?subject=${reportSubject}&body=${reportBody}`;

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-gray-200 px-6 py-4 bg-white">
        <h2 className="text-lg font-bold text-gray-900">{partner}</h2>
        {conversation.job_title && (
          <p className="text-sm text-primary mt-0.5">관련 공고: {conversation.job_title}</p>
        )}
      </div>

      {/* 신고 안내 배너 — 욕설/성희롱/사기 등 부적절한 메시지를 받았을 때의 신고 경로 안내 */}
      <div className="px-6 py-2.5 bg-amber-50 border-b border-amber-100 flex items-start gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-900 leading-relaxed">
          욕설·성희롱·사기 등 <strong>부적절한 메시지</strong>를 받으셨다면 해당 화면을 캡처하여{' '}
          <a
            href={reportMailto}
            className="font-semibold text-amber-700 hover:text-amber-900 underline underline-offset-2"
          >
            {REPORT_EMAIL}
          </a>
          {' '}으로 보내주세요. 신속히 검토하여 조치하겠습니다.
        </p>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50 space-y-3">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-400">
            <MessageCircle className="w-12 h-12 mb-3" />
            <p className="text-sm">첫 메시지를 보내보세요.</p>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === currentUserId;
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[70%] ${mine ? 'items-end' : 'items-start'} flex flex-col`}>
                  <div
                    className={`px-4 py-2 rounded-2xl whitespace-pre-wrap break-words ${
                      mine
                        ? 'bg-primary text-white rounded-br-sm'
                        : 'bg-white border border-gray-200 text-gray-900 rounded-bl-sm'
                    }`}
                  >
                    {m.body}
                  </div>
                  <div className="flex items-center gap-1 mt-1 px-1">
                    {/* 내가 보낸 메시지에만, 상대가 아직 안 읽었으면 '안읽음' 표시 (카카오톡 스타일) */}
                    {mine && !m.read_at && (
                      <span className="text-[10px] font-medium text-accent">안읽음</span>
                    )}
                    <span className="text-[10px] text-gray-400">
                      {new Date(m.created_at).toLocaleTimeString('ko-KR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={handleSubmit} className="border-t border-gray-200 bg-white p-4">
        <div className="flex gap-2 items-end">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e as any);
              }
            }}
            placeholder="메시지를 입력하세요. (Shift+Enter 줄바꿈)"
            rows={2}
            maxLength={2000}
            className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1 shrink-0"
          >
            <Send className="w-4 h-4" />
            전송
          </button>
        </div>
      </form>
    </div>
  );
}
